import React from 'react'
import { Box, Typography } from '@mui/material'

type GlassCardProps = {
  /**
   * Denser fill for panels that sit over bright parts of the backdrop photo,
   * where the default translucency leaves text hard to read.
   */
  dense?: boolean
  title?: React.ReactNode
  right?: React.ReactNode
  children?: React.ReactNode
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sx?: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  contentSx?: any
}

/**
 * The frosted panel every Sentosa insight widget sits in: translucent slate,
 * backdrop blur, hairline border, optional header rail with a drag handle.
 */
const GlassCard = ({
  dense = false,
  title,
  right,
  children,
  sx,
  contentSx,
}: GlassCardProps) => {
  const titleNode =
    typeof title === 'string' ? (
      <Typography noWrap sx={{ color: 'white', fontSize: 18, fontWeight: 700 }}>
        {title}
      </Typography>
    ) : (
      title
    )

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        borderRadius: 3,
        bgcolor: dense ? 'rgba(8,15,26,0.74)' : 'rgba(15,23,42,0.38)',
        backdropFilter: dense
          ? 'blur(24px) saturate(1.1)'
          : 'blur(20px)',
        border: `1px solid ${
          dense ? 'rgba(255,255,255,0.30)' : 'rgba(255,255,255,0.25)'
        }`,
        boxShadow: '0 10px 40px rgba(0,0,0,0.35)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        ...sx,
      }}
    >
      {(title || right) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            columnGap: 1.5,
            rowGap: 1,
            px: 2,
            py: 1.25,
            minHeight: 56,
            flexShrink: 0,
            borderBottom: `1px solid ${
              dense ? 'rgba(255,255,255,0.20)' : 'rgba(255,255,255,0.15)'
            }`,
          }}
        >
          <Box className="drag-handle" sx={{ minWidth: 0, flex: '1 1 160px' }}>
            {titleNode}
          </Box>
          <Box className="nodrag" sx={{ flexShrink: 0 }}>
            {right}
          </Box>
        </Box>
      )}
      <Box
        sx={{
          pointerEvents: 'none',
          position: 'absolute',
          inset: 0,
          borderRadius: 3,
          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)',
        }}
      />
      {!dense && (
        <Box
          sx={{
            pointerEvents: 'none',
            position: 'absolute',
            top: 8,
            left: 8,
            right: 8,
            height: 36,
            borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.10)',
            filter: 'blur(10px)',
          }}
        />
      )}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          minHeight: 0,
          ...contentSx,
        }}
      >
        {children}
      </Box>
    </Box>
  )
}

export default GlassCard
